/**
 * Pure, dependency-free validation for festival map uploads.
 *
 * Mirrors the server-side guards in the `upload-briefing-map` edge function so
 * the browser can reject an oversized or wrong-typed image *before* it spends
 * time pushing megabytes over the wire (the original Sentry incident: a ~36 MB
 * map retried ~7 times, each a doomed upload ending in a 413). The edge
 * function stays the authoritative cap — this is a fast-fail convenience, not
 * the security boundary.
 *
 * Keep `MAX_MAP_BYTES` / `ALLOWED_MAP_MIME` in sync with `MAX_BYTES` /
 * `ALLOWED_MIME` in `supabase/functions/upload-briefing-map/index.ts`.
 */

/** Max festival map size: 10 MiB. Must match MAX_BYTES in the edge function. */
export const MAX_MAP_BYTES = 10 * 1024 * 1024;

/** Image MIME types the edge function accepts. */
export const ALLOWED_MAP_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
]);

const MAX_MAP_MB = Math.round(MAX_MAP_BYTES / (1024 * 1024));

export type FestivalMapValidation = { ok: true } | { ok: false; message: string };

/**
 * Validate a chosen festival map file against the same size / type rules the
 * server enforces. Returns a discriminated result with a user-facing message
 * on failure rather than throwing, so callers can decide how to surface it.
 */
export function validateFestivalMapFile(file: File): FestivalMapValidation {
  if (file.size === 0) {
    return { ok: false, message: "This file is empty. Please choose a different image." };
  }
  if (file.size > MAX_MAP_BYTES) {
    return {
      ok: false,
      message: `Festival map is too large — max ${MAX_MAP_MB} MB. Please upload a smaller image.`,
    };
  }
  const mime = (file.type || "").toLowerCase();
  if (!ALLOWED_MAP_MIME.has(mime)) {
    return { ok: false, message: "Unsupported image type. Use PNG, JPEG, WEBP or GIF." };
  }
  return { ok: true };
}
